import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  fetchGoals, fetchSourceFiles, getReferenceData, invalidateReferenceData, searchMovements,
} from '../../api/endpointsBackend';
import usePhoneHomeCurrency from '../../hooks/usePhoneHomeCurrency';
import { linkedRecords, movementsFilter, recordKey } from './chatRecords';

/**
 * The live data behind the records a conversation is about (MI-343).
 *
 * A message carries a record as its type and its id. Its name, its amount and
 * its date are read here, from the app's own endpoints, so a row shows what is
 * true now and what the reader's role may see: an Advisor's row has no note
 * because the endpoint gives none.
 *
 * The provider wraps a conversation. It knows which records its messages point
 * at, so it loads them together, once: the reference lists, and the movements
 * by id.
 */
const ChatRecordsContext = createContext(null);

// A day-windowed search returns at most 50 days of rows, and each movement can
// be on a day of its own.
const MOVEMENTS_PER_REQUEST = 40;
const NO_RECORD = { status: 'missing', data: null };

const byId = (items) => new Map((items || []).map((item) => [Number(item.id), item]));

const referenceLists = (fresh) => {
  // A category the chat just created is not in a list cached before it existed.
  if (fresh) invalidateReferenceData();
  return getReferenceData().then((ref) => ({
    account: ref.accounts, category: ref.categories, tag: ref.tags,
  }));
};
const goalLists = () => fetchGoals(true).then((goals) => ({ goal: goals }));
const sourceFileLists = () => fetchSourceFiles().then((files) => ({ source_file: files }));

/**
 * Lists of records, by type, loaded when one of them is first wanted. Loaded
 * again when a wanted record is not there (the chat may have just created it),
 * and when `version` moves (the chat changed something).
 */
function useRecordLists(wanted, load, version) {
  const [state, setState] = useState({ lists: null, version: null });
  const retried = useRef(new Set());
  const wantedKey = wanted.map((r) => recordKey(r.type, r.id)).join(',');

  useEffect(() => {
    if (!wantedKey) return undefined;
    const { lists } = state;
    const absent = lists
      ? wanted.filter(({ type, id }) => !lists[type]?.has(id) && !retried.current.has(recordKey(type, id)))
      : [];
    if (lists && state.version === version && absent.length === 0) return undefined;
    absent.forEach(({ type, id }) => retried.current.add(recordKey(type, id)));

    let cancelled = false;
    load(lists !== null)
      .then((loaded) => {
        if (cancelled) return;
        const next = {};
        Object.keys(loaded).forEach((type) => { next[type] = byId(loaded[type]); });
        setState({ lists: next, version });
      })
      // A list that could not be read leaves its records looking "gone" rather
      // than loading for ever.
      .catch(() => { if (!cancelled) setState((prev) => ({ lists: prev.lists || {}, version })); });
    return () => { cancelled = true; };
    // `wanted` is `wantedKey` as a list; the key is what changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wantedKey, version, state, load]);

  return state.lists;
}

/** Movements by id: `Map<id, movement | null>`, null once known to be gone. */
function useMovements(ids, changedIds, version) {
  const [movements, setMovements] = useState(() => new Map());
  const requested = useRef(new Set());
  const seenVersion = useRef(version);
  const idsKey = ids.join(',');

  useEffect(() => {
    // The chat changed some of them: what is on screen is the old reading.
    if (seenVersion.current !== version) {
      seenVersion.current = version;
      changedIds.forEach((id) => requested.current.delete(id));
    }
    const pending = ids.filter((id) => !requested.current.has(id));
    if (pending.length === 0) return;
    pending.forEach((id) => requested.current.add(id));

    for (let i = 0; i < pending.length; i += MOVEMENTS_PER_REQUEST) {
      const chunk = pending.slice(i, i + MOVEMENTS_PER_REQUEST);
      searchMovements([movementsFilter(chunk)], null, null, null, 50, 0)
        .then((data) => {
          const found = byId(data?.items);
          setMovements((prev) => {
            const next = new Map(prev);
            chunk.forEach((id) => next.set(id, found.get(id) || null));
            return next;
          });
        })
        .catch(() => {
          setMovements((prev) => {
            const next = new Map(prev);
            chunk.forEach((id) => { if (!next.has(id)) next.set(id, null); });
            return next;
          });
        });
    }
    // `ids` and `changedIds` are read through their keys.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsKey, version]);

  return movements;
}

/** Which records a conversation points at, and which ones the chat changed. */
export const recordsIn = (messages) => {
  const wanted = new Map();
  const changed = [];
  const want = (type, id) => wanted.set(recordKey(type, id), { type, id: Number(id) });
  (messages || []).forEach((message) => {
    if (message.role !== 'assistant') return;
    linkedRecords(message.content).forEach((r) => want(r.type, r.id));
    (message.records || []).forEach((r) => {
      want(r.type, r.id);
      if (r.action) changed.push({ type: r.type, id: Number(r.id) });
    });
  });
  return { wanted: [...wanted.values()], changed };
};

/**
 * `onOpenRecord(type, id, { month })` and `onOpenSearch({ filters, start, end })`
 * are what a click does; the host decides, since the floating panel, the full page and
 * the phone each go about it differently. `openKey` is the record now open
 * beside the chat, which its chip and its row show.
 */
export function ChatRecordsProvider({
  messages, onOpenRecord, onOpenSearch, openKey = null, phone = false, children,
}) {
  const { wanted, changed } = useMemo(() => recordsIn(messages), [messages]);
  // On the phone a row drops the code of the home currency, as every phone
  // list of movements does.
  const homeCurrency = usePhoneHomeCurrency();
  const version = changed.map((r) => recordKey(r.type, r.id)).join(',');
  const ofTypes = (...types) => wanted.filter((r) => types.includes(r.type));

  const reference = useRecordLists(ofTypes('account', 'category', 'tag'), referenceLists, version);
  const goals = useRecordLists(ofTypes('goal'), goalLists, version);
  const files = useRecordLists(ofTypes('source_file'), sourceFileLists, version);
  const movements = useMovements(
    ofTypes('movement').map((r) => r.id),
    changed.filter((r) => r.type === 'movement').map((r) => r.id),
    version,
  );

  const lookup = useCallback((type, id) => {
    const key = Number(id);
    if (type === 'movement') {
      if (!movements.has(key)) return { status: 'loading', data: null };
      const movement = movements.get(key);
      return movement ? { status: 'ready', data: movement } : NO_RECORD;
    }
    const lists = type === 'goal' ? goals : type === 'source_file' ? files : reference;
    if (!lists) return { status: 'loading', data: null };
    const data = lists[type]?.get(key);
    return data ? { status: 'ready', data } : NO_RECORD;
  }, [movements, reference, goals, files]);

  const value = useMemo(
    () => ({ messages, lookup, onOpenRecord, onOpenSearch, openKey, phone, homeCurrency }),
    [messages, lookup, onOpenRecord, onOpenSearch, openKey, phone, homeCurrency],
  );
  return <ChatRecordsContext.Provider value={value}>{children}</ChatRecordsContext.Provider>;
}

const OUTSIDE = {
  messages: [], lookup: () => NO_RECORD, onOpenRecord: null, onOpenSearch: null, openKey: null,
  phone: false, homeCurrency: null,
};

/**
 * What the conversation around offers: its `messages`, `lookup`, what a click
 * does, the open record.
 */
export const useChatRecords = () => useContext(ChatRecordsContext) || OUTSIDE;

/** `{ status: 'loading' | 'ready' | 'missing', data }` for one record. */
export const useChatRecord = (type, id) => useChatRecords().lookup(type, id);
